import { useMemo, useState, Fragment } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Select, Input, Segmented, Drawer } from '../../ui';
import { request } from '../../api/client';
import { fmtQty } from '../../lib/format';
import { formatIst } from '../../lib/time';
import { Pill } from '../../components/TwDataTable';
import { MasterTag } from '../../lib/warehouseLabel';
import { ReturnDetailPanel } from '../returns/ReturnDetailPage';

/**
 * RETURNED-STOCK REGISTER — every returned sub-lot (sellable AND held), traceable to its PRINTED (original)
 * batch, warehouse-wise. Printed-batch-first: users see the batch on the packet, not the internal RET- lot
 * (which is demoted to a small "system lot" in the drill-down). Read-only; the backend scopes a WHM to their
 * warehouse(s). Data from GET /returns/received-stock (return_line_receipts, enriched).
 */

const inr = (v: any) => `₹${Number(v ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

// Condition → user-facing label + house Pill colour.
const COND: Record<string, { label: string; pill: string }> = {
  RESALABLE:   { label: 'Sellable',    pill: 'green' },
  NEAR_EXPIRY: { label: 'Near expiry', pill: 'lime' },
  DAMAGED:     { label: 'Damaged',     pill: 'orange' },
  DEFECTIVE:   { label: 'Defective',   pill: 'gold' },
  MISMATCH:    { label: 'Batch mismatch', pill: 'gold' },
  EXPIRED:     { label: 'Expired',     pill: 'red' },
  BLOCKED:     { label: 'Hold / recall', pill: 'purple' },
};
const condLabel = (c: string) => COND[c]?.label ?? c;
const condPill = (c: string) => COND[c]?.pill ?? 'default';

function Chevron({ open }: { open: boolean }) {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2"
      className={`h-3.5 w-3.5 shrink-0 text-subtle transition-transform duration-150 ${open ? 'rotate-90' : ''}`}>
      <path d="M6 4l4 4-4 4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const TH = 'px-3 py-2.5 text-left text-2xs font-semibold uppercase tracking-wide text-muted';
const THR = `${TH} text-right`;

type Row = {
  id: string; qty: number; condition: string; sellable: boolean;
  productId: string; productName: string;
  originalBatch: string | null; internalBatch: string | null; landedStatus: string | null; expDate: string | null;
  warehouseId: string; warehouse: string;
  returnId: string; returnNo: string; returnDate: string | null;
  retailerId: string | null; retailer: string | null;
  receivedByName: string | null; receivedAt: string | null; value: number;
};

export function ReturnedStockPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['received-stock'],
    queryFn: () => request<{ rows: Row[]; truncated?: boolean }>({ url: '/returns/received-stock' }),
  });
  const rows = data?.rows ?? [];
  // Master warehouse ids for the filter capsule — best-effort (degrades to no capsule without WAREHOUSE.VIEW).
  const { data: whAll } = useQuery({ queryKey: ['warehouses-all'], queryFn: () => request<any[]>({ url: '/warehouses' }), staleTime: 5 * 60_000, retry: false });
  const masterIds = useMemo(() => new Set((whAll ?? []).filter((w: any) => w.isMaster).map((w: any) => String(w.id))), [whAll]);

  const [wh, setWh] = useState<string | undefined>();
  const [cond, setCond] = useState<string | undefined>();
  const [view, setView] = useState<'all' | 'sellable' | 'held'>('all');
  const [q, setQ] = useState('');
  const [open, setOpen] = useState<Set<string>>(new Set());
  const toggle = (k: string) => setOpen((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const [openReturn, setOpenReturn] = useState<string | null>(null);

  const warehouses = useMemo(() => [...new Map(rows.map((r) => [r.warehouseId, r.warehouse])).entries()], [rows]);
  const conditions = useMemo(() => [...new Set(rows.map((r) => r.condition))], [rows]);

  const filtered = useMemo(() => rows.filter((r) => {
    if (wh && r.warehouseId !== wh) return false;
    if (cond && r.condition !== cond) return false;
    if (view === 'sellable' && !r.sellable) return false;
    if (view === 'held' && r.sellable) return false;
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      const hay = `${r.productName} ${r.originalBatch ?? ''} ${r.internalBatch ?? ''} ${r.returnNo ?? ''} ${r.retailer ?? ''}`.toLowerCase();
      if (!hay.includes(s)) return false;
    }
    return true;
  }), [rows, wh, cond, view, q]);

  // Group warehouse-wise by (product + printed batch): one row per product/batch/warehouse with a condition breakdown.
  const groups = useMemo(() => {
    const m = new Map<string, {
      key: string; productId: string; productName: string; originalBatch: string | null; warehouse: string;
      qty: number; value: number; sellableQty: number; heldQty: number; byCond: Map<string, number>; items: Row[];
    }>();
    for (const r of filtered) {
      const key = `${r.productId}|${r.originalBatch ?? '—'}|${r.warehouseId}`;
      let g = m.get(key);
      if (!g) { g = { key, productId: r.productId, productName: r.productName, originalBatch: r.originalBatch, warehouse: r.warehouse, qty: 0, value: 0, sellableQty: 0, heldQty: 0, byCond: new Map(), items: [] }; m.set(key, g); }
      g.qty += r.qty; g.value += r.value;
      if (r.sellable) g.sellableQty += r.qty; else g.heldQty += r.qty;
      g.byCond.set(r.condition, (g.byCond.get(r.condition) ?? 0) + r.qty);
      g.items.push(r);
    }
    return [...m.values()].sort((a, b) => b.heldQty - a.heldQty || b.qty - a.qty);
  }, [filtered]);

  const totalUnits = filtered.reduce((s, r) => s + r.qty, 0);
  const sellableUnits = filtered.filter((r) => r.sellable).reduce((s, r) => s + r.qty, 0);
  const heldUnits = totalUnits - sellableUnits;
  const totalValue = filtered.reduce((s, r) => s + r.value, 0);

  const kpi = (label: string, value: string, tone = 'text-content') => (
    <div className="rounded-card border border-line bg-surface p-3 shadow-soft">
      <div className="text-xs text-muted">{label}</div>
      <div className={`mt-0.5 text-xl font-semibold ${tone} tabular-nums`}>{value}</div>
    </div>
  );

  return (
    <div>
      {data?.truncated && (
        <div className="mb-2 rounded-control border border-warning-500/30 bg-warning-500/10 px-3 py-2 text-xs text-content">
          Showing the 5,000 most recent returned receipts — older ones aren’t listed here. Filter by warehouse / product to focus, or ask for full history export if you need everything.
        </div>
      )}
      <div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
        {kpi('Returned units', fmtQty(totalUnits))}
        {kpi('Sellable (back in stock)', fmtQty(sellableUnits), 'text-brand-700 dark:text-brand-400')}
        {kpi('Held (not sellable)', fmtQty(heldUnits), 'text-danger-600')}
        {kpi('Value (dealer)', inr(totalValue))}
      </div>

      <div className="overflow-hidden rounded-card border border-line bg-surface shadow-soft">
        <div className="flex flex-wrap items-center gap-2 p-3">
          <Input placeholder="Search product / batch / return / retailer" value={q} onChange={(e) => setQ(e.target.value)} style={{ width: 300 }} />
          <Select allowClear placeholder="All warehouses" value={wh} onChange={setWh} style={{ width: 200 }}
            options={warehouses.map(([id, name]) => ({ value: id, label: masterIds.has(String(id)) ? <span className="inline-flex items-center">{name}<MasterTag /></span> : name }))} />
          <Select allowClear placeholder="Any condition" value={cond} onChange={setCond} style={{ width: 170 }}
            options={conditions.map((c) => ({ value: c, label: condLabel(c) }))} />
          <Segmented value={view} onChange={(v) => setView(v as any)}
            options={[{ label: 'All', value: 'all' }, { label: 'Sellable', value: 'sellable' }, { label: 'Held', value: 'held' }]} />
        </div>

        <div className="overflow-x-auto border-t border-line">
          {isLoading ? (
            <div className="space-y-2 p-3">{[...Array(5)].map((_, i) => <div key={i} className="h-10 animate-pulse rounded bg-surface-3" />)}</div>
          ) : groups.length === 0 ? (
            <div className="px-4 py-12 text-center text-sm text-subtle">No returned stock{q || wh || cond || view !== 'all' ? ' matches these filters' : ' yet'}.</div>
          ) : (
            <table className="w-full border-collapse text-sm">
              <thead className="bg-surface-2">
                <tr>
                  <th className="w-8 px-2" />
                  <th className={TH}>Product</th>
                  <th className={TH}>Printed batch</th>
                  <th className={TH}>Warehouse</th>
                  <th className={TH}>Condition breakdown</th>
                  <th className={THR}>Held</th>
                  <th className={THR}>Total</th>
                  <th className={THR}>Value</th>
                </tr>
              </thead>
              <tbody>
                {groups.map((g) => {
                  const isOpen = open.has(g.key);
                  return (
                    <Fragment key={g.key}>
                      <tr className="cursor-pointer border-t border-line hover:bg-surface-2" onClick={() => toggle(g.key)}>
                        <td className="px-2 align-middle"><Chevron open={isOpen} /></td>
                        <td className="px-3 py-2.5 align-middle font-medium text-content">{g.productName}</td>
                        <td className="px-3 py-2.5 align-middle font-mono text-xs text-content">{g.originalBatch ?? <span className="text-subtle">— (unspecified)</span>}</td>
                        <td className="px-3 py-2.5 align-middle text-muted">{g.warehouse}</td>
                        <td className="px-3 py-2.5 align-middle">
                          <div className="flex flex-wrap gap-1">
                            {[...g.byCond.entries()].sort((a, b) => b[1] - a[1]).map(([c, qty]) => (
                              <Pill key={c} color={condPill(c)}>{fmtQty(qty)} {condLabel(c)}</Pill>
                            ))}
                          </div>
                        </td>
                        <td className={`px-3 py-2.5 text-right align-middle tabular-nums ${g.heldQty > 0 ? 'font-semibold text-danger-600' : 'text-subtle'}`}>{g.heldQty > 0 ? fmtQty(g.heldQty) : '—'}</td>
                        <td className="px-3 py-2.5 text-right align-middle font-semibold text-content tabular-nums">{fmtQty(g.qty)}</td>
                        <td className="px-3 py-2.5 text-right align-middle text-content tabular-nums">{inr(g.value)}</td>
                      </tr>
                      {isOpen && (
                        <tr className="border-t border-line bg-surface-2/60">
                          <td />
                          <td colSpan={7} className="px-3 py-2">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="text-2xs uppercase tracking-wide text-subtle">
                                  <th className="py-1 text-left font-medium">Return</th>
                                  <th className="py-1 text-left font-medium">Received</th>
                                  <th className="py-1 text-left font-medium">Condition</th>
                                  <th className="py-1 text-right font-medium">Qty</th>
                                  <th className="py-1 text-left font-medium">Retailer</th>
                                  <th className="py-1 text-left font-medium">Received by</th>
                                  <th className="py-1 text-left font-medium">System lot</th>
                                </tr>
                              </thead>
                              <tbody>
                                {g.items.map((it) => (
                                  <tr key={it.id} className="border-t border-line/60">
                                    <td className="py-1"><button type="button" onClick={() => setOpenReturn(it.returnId)} className="font-medium text-brand-700 hover:underline dark:text-brand-400">{it.returnNo}</button></td>
                                    <td className="py-1 text-muted">{it.receivedAt ? formatIst(it.receivedAt) : (it.returnDate ? String(it.returnDate).slice(0, 10) : '—')}</td>
                                    <td className="py-1"><Pill color={condPill(it.condition)}>{condLabel(it.condition)}</Pill></td>
                                    <td className="py-1 text-right tabular-nums text-content">{fmtQty(it.qty)}</td>
                                    <td className="py-1 text-muted">{it.retailer ?? '—'}</td>
                                    <td className="py-1 text-muted">{it.receivedByName ?? '—'}</td>
                                    <td className="py-1 font-mono text-2xs text-subtle" title="Internal system lot — you don't need this; search by the printed batch above">{it.internalBatch ?? '—'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
      <p className="mt-2 text-xs text-subtle">
        Printed batch = the number on the packet. “System lot” (in the row detail) is internal — you never need to type it; search by the printed batch, product, return, or retailer above.
      </p>

      <Drawer title="Return details" width={620} open={!!openReturn} onClose={() => setOpenReturn(null)}>
        {openReturn && <ReturnDetailPanel id={openReturn} onClosed={() => setOpenReturn(null)} />}
      </Drawer>
    </div>
  );
}
